import { Icon } from "@mui/material";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";

import { useFormatMessage } from "lang";
import { isFirefox, isIOS, isIOSSafari, isPWA } from "utils/detectDevice";

import { BeforeInstallPromptEvent } from "./types";

export default function PWAInfo() {
  const formatMessage = useFormatMessage();

  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  const iosSafari = isIOSSafari() && !isPWA();
  const iosNotSafari = isIOS() && !isIOSSafari() && !isPWA();
  const firefox = isFirefox() && !isPWA();

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener(
      "beforeinstallprompt",
      handleBeforeInstallPrompt as EventListener,
    );

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt as EventListener,
      );
    };
  }, []);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then(() => {
        setDeferredPrompt(null);
      });
    }
  };

  if (isPWA()) return null;
  if (!isInstallable && !iosSafari && !iosNotSafari && !firefox) return null;

  const getInfoContent = () => {
    if (iosNotSafari) {
      return formatMessage("pwa.iosNotSafari");
    }
    if (iosSafari) {
      return (
        <>
          {formatMessage("pwa.iosInfo", {
            values: { shareIcon: "\u200B" },
          })
            .split("\u200B")
            .map((part, i, arr) => (
              <span key={i}>
                {part}
                {i < arr.length - 1 && (
                  <Icon sx={{ fontSize: 16, verticalAlign: "middle" }}>
                    ios_share
                  </Icon>
                )}
              </span>
            ))}
        </>
      );
    }
    if (firefox) {
      return formatMessage("pwa.firefoxInfo");
    }
    return formatMessage("pwa.info");
  };

  return (
    <Card variant="outlined" sx={{ m: 3, p: 3, height: "min-content" }}>
      <CardContent>
        <Icon fontSize="small">auto_awesome</Icon>
        <Typography gutterBottom sx={{ fontWeight: 600 }}>
          {formatMessage("pwa.appAvailable")}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          {getInfoContent()}
        </Typography>
        {isInstallable && (
          <Button
            onClick={handleInstallClick}
            variant="contained"
            size="small"
            fullWidth
          >
            {formatMessage("pwa.getTheApp")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
